import React from 'react';
import { NamedLink } from '../../../components';
import css from './SeoCarInfoCard.module.css';

/**
 * Clean car-spec panel for the SEO pages, styled to match the SearchPage
 * CarInfoSidebar (the reg-search box on /annonser): typography-led spec rows
 * plus clickable dimension pills. The pills link straight into the live
 * listings page with the right filters applied.
 */

// Parse a tire dimension like "235/55R19" into listing filter params.
const tireToParams = dim => {
  const m = /(\d+)\/(\d+)\s*Z?R(\d+)/.exec(String(dim));
  if (!m) return null;
  return { tireWidthFront: m[1], profileFront: m[2], diameter: m[3] };
};

// Parse a rim spec like "8JX18" → diameter (the part after x).
const rimToDiameter = rim => {
  const m = /[xX]\s*(\d+)/.exec(String(rim));
  return m ? m[1] : null;
};

const SpecRow = ({ label, value }) =>
  value ? (
    <div className={css.specRow}>
      <dt className={css.specLabel}>{label}</dt>
      <dd className={css.specValue}>{value}</dd>
    </div>
  ) : null;

const SeoCarInfoCard = ({ fitment, pageType, makeSlug, modelSlug }) => {
  const yearValue = fitment.yearFrom
    ? `${fitment.yearFrom}–${fitment.yearTo || 'nu'}`
    : fitment.yearTo || null;

  const offsetFront = fitment.offsets?.front || {};
  const offsetValue = offsetFront.oem != null ? `ET${offsetFront.oem}` : null;

  const hasSpecs =
    fitment.boltPattern || fitment.centerBoreMm || offsetValue || fitment.studThread || yearValue;

  // Dimension pills: tire sizes on tire pages, rim sizes on rim pages.
  const tireDims = (fitment.tires?.all || []).filter(Boolean);
  const rimDims = String(fitment.rims?.all?.optimal || '')
    .split('|')
    .map(s => s.trim())
    .filter(Boolean);

  const buildSearch = params => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') usp.set(k, v);
    });
    return `?${usp.toString()}`;
  };

  const dimLabel = pageType === 'rim' ? 'Rekommenderade fälgdimensioner' : 'Rekommenderade däckdimensioner';

  const renderDimPills = () => {
    if (pageType === 'rim') {
      return rimDims.slice(0, 8).map(rim => {
        const diameter = rimToDiameter(rim);
        return (
          <NamedLink
            key={rim}
            className={css.dimButton}
            name="SearchPageMakeModel"
            params={{ make: makeSlug, model: modelSlug }}
            to={{ search: buildSearch({ pub_adType: 'rim', pub_diameter: diameter }) }}
            title="Visa annonser"
          >
            {rim.replace(/\.00/g, '')}
          </NamedLink>
        );
      });
    }
    return tireDims.slice(0, 8).map(dim => {
      const p = tireToParams(dim);
      return (
        <NamedLink
          key={dim}
          className={css.dimButton}
          name="SearchPageMakeModel"
          params={{ make: makeSlug, model: modelSlug }}
          to={{
            search: buildSearch({
              pub_adType: 'tire',
              ...(p
                ? {
                    pub_tireWidthFront: p.tireWidthFront,
                    pub_profileFront: p.profileFront,
                    pub_diameter: p.diameter,
                  }
                : {}),
            }),
          }}
          title="Visa annonser"
        >
          {dim}
        </NamedLink>
      );
    });
  };

  return (
    <section className={css.root} aria-label={`Bilinfo för ${fitment.make} ${fitment.model}`}>
      <h2 className={css.title}>
        {fitment.make} {fitment.model}
        {fitment.variant ? ` ${fitment.variant}` : ''}
        {yearValue ? ` · ${yearValue}` : ''}
      </h2>

      {hasSpecs ? (
        <dl className={css.specs}>
          <SpecRow label="Bultcirkel" value={fitment.boltPattern} />
          <SpecRow
            label="Navhål / CB"
            value={fitment.centerBoreMm ? `${fitment.centerBoreMm} mm` : null}
          />
          <SpecRow label="Offset / ET" value={offsetValue} />
          <SpecRow label="Gänga" value={fitment.studThread} />
        </dl>
      ) : null}

      {(pageType === 'rim' ? rimDims : tireDims).length > 0 ? (
        <div className={css.group}>
          <span className={css.groupLabel}>{dimLabel}</span>
          <div className={css.dimList}>{renderDimPills()}</div>
        </div>
      ) : null}
    </section>
  );
};

export default SeoCarInfoCard;
